/**
 * Finds the links in a piece of text, so an answer can render them as links.
 *
 * Two shapes are recognised: a bare address (`https://…`, `http://…`, `www.…`) and a
 * markdown link (`[label](https://…)`), which models write often. Only http and https ever
 * become links — the text comes from a model, and `javascript:` in an href is script.
 */

export type TextPart = { type: 'text'; value: string } | { type: 'link'; href: string; label: string }

// A markdown link, whose URL may hold one level of brackets (`…/Foo_(bar)`), or a bare
// address. Global and case-insensitive: `HTTPS://` and `WWW.` are still addresses.
const LINK =
  /\[([^\]\n]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)|((?:https?:\/\/|www\.)[^\s<>"'`]+)/gi

// Sentence punctuation that follows an address but is not part of it: "see https://x.com."
const TRAILING = /[.,;:!?'"*_]$/

const CLOSERS: Record<string, string> = { ')': '(', ']': '[', '}': '{' }

function count(text: string, char: string): number {
  return text.split(char).length - 1
}

/** Drop punctuation the address picked up from the sentence around it. */
function trimAddress(raw: string): string {
  let address = raw
  for (;;) {
    const last = address.at(-1) ?? ''
    if (TRAILING.test(last)) {
      address = address.slice(0, -1)
      continue
    }
    // A closing bracket belongs to the address only if the address opened it:
    // "(see https://x.com/a)" versus "https://en.wikipedia.org/wiki/Foo_(bar)".
    const opener = CLOSERS[last]
    if (opener && count(address, last) > count(address, opener)) {
      address = address.slice(0, -1)
      continue
    }
    return address
  }
}

/** The href to use, or null when this must not be a link. */
function safeHref(raw: string): string | null {
  const candidate = /^www\./i.test(raw) ? `https://${raw}` : raw
  try {
    const { protocol } = new URL(candidate)
    return protocol === 'http:' || protocol === 'https:' ? candidate : null
  } catch {
    return null
  }
}

export function splitLinks(text: string): TextPart[] {
  const parts: TextPart[] = []
  const pushText = (value: string) => {
    if (!value) return
    const previous = parts.at(-1)
    if (previous?.type === 'text') previous.value += value
    else parts.push({ type: 'text', value })
  }

  let cursor = 0
  for (const match of text.matchAll(LINK)) {
    const start = match.index ?? 0
    pushText(text.slice(cursor, start))
    const [whole, label, markdownHref, bare] = match

    if (bare !== undefined) {
      const address = trimAddress(bare)
      const href = safeHref(address)
      if (href) parts.push({ type: 'link', href, label: address })
      else pushText(address)
      pushText(bare.slice(address.length))
    } else {
      const href = safeHref(markdownHref ?? '')
      // An unsafe target keeps its words and loses the link.
      if (href) parts.push({ type: 'link', href, label: label ?? href })
      else pushText(label ?? whole)
    }
    cursor = start + whole.length
  }
  pushText(text.slice(cursor))
  return parts
}
